import { ATK_MAP_PALETTE, getTaskActivity } from "./activityColors";
import {
  BoxSides,
  lineToBox,
  symmetricSides,
} from "common/utils/map/lineToBox";
import { Feature, FeatureCollection, LineString, Polygon } from "geojson";
import {
  getFollowPathDisplayPath,
  TASK_BASE_PROPS,
  TaskFeatureProperties,
} from "common/utils/rtcJobs";
import { Layer, Source } from "react-map-gl";
import { makeTaskRenderer, TaskWith } from "../TaskLayers";
import { MapImageId } from "../types";
import { RigEffectiveConfig } from "protos/atk/effective_config";
import { State, TaskDesignation } from "protos/rtc/jobs";
import { resolvedTheme as theme } from "common/theme/tailwind.theme";
import React from "react";

// This is a fallback for when there is truly no width data supplied to this component.
// It helps when troubleshooting because a lack of rig or job config data will
// result in very obviously weird looking tasks.
export const DEFAULT_ROW_WIDTH = 0.5;

// Default width for Transport jobs. We don't have a rig for these, but we do want to
// give them a reasonable width so they're visible on the map.
export const DEFAULT_TRANSPORT_WIDTH = 6;

interface LineFeatureProperties extends TaskFeatureProperties {
  lineColor: string;
}

interface SwathFeatureProperties extends TaskFeatureProperties {
  fillColor: string;
  isCovered: boolean;
}

interface InProgressFeatureProperties extends TaskFeatureProperties {
  isMaybeLifted: boolean;
}

// boundary x is lateral, right positive, matching how the map draws the tractor
const getTractorSides = (
  rig: RigEffectiveConfig | undefined
): BoxSides | undefined => {
  const xs = rig?.tractor?.boundary?.points.map((p) => p.x) ?? [];
  if (xs.length > 0) {
    const sides = { leftM: -Math.min(...xs), rightM: Math.max(...xs) };
    if (sides.leftM + sides.rightM > 0) {
      return sides;
    }
  }
  const wheelSpacing = rig?.tractor?.wheelSpacingM ?? 0;
  return wheelSpacing > 0 ? symmetricSides(wheelSpacing) : undefined;
};

const getImplementSides = (
  rig: RigEffectiveConfig | undefined
): BoxSides | undefined => {
  const implement = rig?.implements.at(0);
  if (!implement) {
    return;
  }
  const { workingWidthMLeft: leftM, workingWidthMRight: rightM } = implement;
  if (leftM + rightM > 0) {
    return { leftM, rightM };
  }
  return implement.workingWidthM > 0
    ? symmetricSides(implement.workingWidthM)
    : undefined;
};

enum LayerIds {
  CENTERLINE = "fpt-centerline",
  CENTERLINE_ARROWS = "fpt-centerline-arrows",
  FILL = "fpt-fill",
  IN_PROGRESS = "fpt-in-progress",
  PROGRESS_LINE = "fpt-progress-line",
}

export const renderFollowPathTask = makeTaskRenderer({
  detailsField: "followPath",
  Render: React.memo(
    ({
      configuredJob,
      tasks,
      extraLayerProps,
      beforeId,
      rig,
      followPathDisplay,
    }) => {
      const implementSides =
        getImplementSides(rig) ??
        symmetricSides(
          configuredJob.groundPrep?.implementDefinition?.widthM ??
            (configuredJob.transport
              ? DEFAULT_TRANSPORT_WIDTH
              : DEFAULT_ROW_WIDTH)
        );
      const tractorSides = getTractorSides(rig) ?? implementSides;
      const isWorking = (t: TaskWith<"followPath">): boolean =>
        t.metadata?.designation === TaskDesignation.TASK_DESIGNATION_WORK;
      // by task, not path drawn: headland turns only carry a front axle path
      const isMaybeLifted = (t: TaskWith<"followPath">): boolean =>
        followPathDisplay === "implement" && !isWorking(t);
      const boxSides = (isImplement: boolean): BoxSides =>
        isImplement ? implementSides : tractorSides;
      const displayPaths = tasks.flatMap((task) => {
        const displayPath = getFollowPathDisplayPath(
          task.followPath,
          followPathDisplay
        );
        if (!displayPath) {
          return [];
        }
        const isImplement = displayPath.rigPoint === "implement";
        const coordinates = displayPath.path.points.map((pt) => [
          pt.lng,
          pt.lat,
        ]);
        return {
          task,
          coordinates,
          progressCoordinates: task.progressPath?.points.map((pt) => [
            pt.lng,
            pt.lat,
          ]),
          isImplement,
          box: lineToBox(coordinates, boxSides(isImplement)),
        };
      });

      const centerlineData: FeatureCollection<
        LineString,
        LineFeatureProperties
      > = {
        type: "FeatureCollection",
        features: displayPaths.map(({ task, coordinates }) => {
          const { covered, uncovered } = ATK_MAP_PALETTE[getTaskActivity(task)];
          return {
            type: "Feature",
            geometry: { type: "LineString", coordinates },
            properties: {
              ...TASK_BASE_PROPS,
              task,
              lineColor:
                task.state === State.COMPLETED ? covered.line : uncovered.line,
            },
          };
        }),
      };

      const swathData: FeatureCollection<Polygon, SwathFeatureProperties> = {
        type: "FeatureCollection",
        features: displayPaths.flatMap(
          ({
            task,
            coordinates,
            progressCoordinates,
            isImplement,
          }): Feature<Polygon, SwathFeatureProperties>[] => {
            const geometry = lineToBox(
              progressCoordinates ?? coordinates,
              boxSides(isImplement)
            );
            if (!geometry) {
              return [];
            }
            const isCovered =
              progressCoordinates !== undefined ||
              task.state === State.COMPLETED;
            const { covered, uncovered } =
              ATK_MAP_PALETTE[getTaskActivity(task)];
            return [
              {
                type: "Feature",
                geometry,
                properties: {
                  ...TASK_BASE_PROPS,
                  task,
                  fillColor: isCovered ? covered.swath : uncovered.swath,
                  isCovered,
                },
              },
            ];
          }
        ),
      };

      const progressLineData: FeatureCollection<
        LineString,
        LineFeatureProperties
      > = {
        type: "FeatureCollection",
        features: displayPaths.flatMap(
          ({
            task,
            progressCoordinates,
          }): Feature<LineString, LineFeatureProperties>[] => {
            if (
              !progressCoordinates ||
              progressCoordinates.length < 2 ||
              task.state === State.COMPLETED
            ) {
              return [];
            }
            return [
              {
                type: "Feature",
                geometry: {
                  type: "LineString",
                  coordinates: progressCoordinates,
                },
                properties: {
                  ...TASK_BASE_PROPS,
                  task,
                  lineColor:
                    ATK_MAP_PALETTE[getTaskActivity(task)].covered.line,
                },
              },
            ];
          }
        ),
      };

      // under the fill, so the hatch only shows where the task isn't covered yet
      const inProgressData: FeatureCollection<
        Polygon,
        InProgressFeatureProperties
      > = {
        type: "FeatureCollection",
        features: displayPaths.flatMap(
          ({ task, box }): Feature<Polygon, InProgressFeatureProperties>[] => {
            if (!box || task.state !== State.IN_PROGRESS) {
              return [];
            }
            return [
              {
                type: "Feature",
                geometry: box,
                properties: {
                  ...TASK_BASE_PROPS,
                  task,
                  isMaybeLifted: isMaybeLifted(task),
                },
              },
            ];
          }
        ),
      };

      return (
        <>
          <Source data={centerlineData} type="geojson">
            <Layer
              id={LayerIds.CENTERLINE}
              beforeId={beforeId}
              type="line"
              paint={{ "line-color": ["get", "lineColor"], "line-width": 4 }}
              {...extraLayerProps}
            />
            <Layer
              id={LayerIds.CENTERLINE_ARROWS}
              beforeId={beforeId}
              type="symbol"
              layout={{
                "symbol-placement": "line-center",
                "text-allow-overlap": true,
                "text-field": "▶",
                "text-keep-upright": false,
                "text-rotation-alignment": "map",
                "text-size": [
                  "interpolate",
                  ["exponential", 2],
                  ["zoom"],
                  15,
                  12,
                  18,
                  24,
                  21,
                  64,
                ],
              }}
              paint={{
                "text-color": theme.colors.black,
                "text-opacity": 0.8,
              }}
              {...extraLayerProps}
            />
          </Source>
          <Source data={progressLineData} type="geojson">
            <Layer
              id={LayerIds.PROGRESS_LINE}
              beforeId={LayerIds.CENTERLINE_ARROWS}
              type="line"
              paint={{ "line-color": ["get", "lineColor"], "line-width": 4 }}
              {...extraLayerProps}
            />
          </Source>
          <Source data={swathData} type="geojson">
            <Layer
              id={LayerIds.FILL}
              beforeId={LayerIds.CENTERLINE}
              type="fill"
              paint={{
                "fill-color": ["get", "fillColor"],
                "fill-opacity": ["case", ["get", "isCovered"], 0.4, 0.2],
              }}
              {...extraLayerProps}
            />
          </Source>
          <Source data={inProgressData} type="geojson">
            <Layer
              id={LayerIds.IN_PROGRESS}
              beforeId={LayerIds.FILL}
              type="fill"
              paint={{
                "fill-pattern": MapImageId.TextureInProgress,
                "fill-opacity": ["case", ["get", "isMaybeLifted"], 0.8, 1],
              }}
              {...extraLayerProps}
            />
          </Source>
        </>
      );
    }
  ),
  interactiveLayerIds: [LayerIds.FILL],
  imagesToLoad: [MapImageId.TextureInProgress],
});
